<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit, { type ITableColumn, type ITableSortSettings, type TTableRow } from '@pocketprep/ui-kit'
import { analyticsModule } from '@/store/analytics/module'
import { isSupportWindow } from '@/utils'
import { organizationModule } from '@/store/organization/module'
import IntegrationCard from '@/components/Admin/Integrations/IntegrationCard.vue'
import { integrationsModule } from '@/store/integrations/module'
import AddIntegrationToCourseSidePanel from '@/components/SidePanels/Integrations/AddIntegrationToCourse.vue'
import EditCourseIntegrationSidePanel from '@/components/SidePanels/Integrations/EditCourseIntegration.vue'
import UnarchiveIntegrationSidePanel from '@/components/SidePanels/Integrations/UnarchiveIntegration.vue'
import { instructorModule } from '@/store/instructor/module'
import { groupModule } from '@/store/group/module'
import { examModule } from '@/store/exam/module'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'

const {
    Tag,
    EmptyState,
    Button: PocketButton,
    TablePagination,
    Table,
    OverflowTooltip,
    Tooltip,
    Icon,
} = UIKit

type MappedIntegration = {
    id: string
    courseName: string
    activatedBy: string
    exam: string
    orgExamId: string
    group: string
    orgGroupId: string
    instructorIds: string[]
    instructorString: string
    isArchived: boolean
    styles: { [key: string]: string }
}

type CourseIntegrationLib = {
    [ integrationId: string ]: {
        orgExamId: string
        orgGroupId: string
        instructors: string[]
    }
}

type IntegrationActionParams = {
    orgExamId: string
    courseIntegrationId: string
    instructorIds: string[]
    group: {
        label: string
        value: string
    }
}

const rowHoverId = ref<string | number | null>(null)
const cellHovered = ref<null | { propName: string; id: string }>(null)

// Integrations Table
const tableColumns: ITableColumn[] = [{
    name: 'Course Name',
    propName: 'courseName',
    styles: {
        width: '180px',
    },
}, {
    name: 'Activated By',
    propName: 'activatedBy',
    styles: {
        width: '127px',
    },
}, {
    name: 'Exam',
    propName: 'exam',
    styles: {
        width: '127px',
    },
}, {
    name: 'Group',
    propName: 'group',
    styles: {
        width: '127px',
    },
}, {
    name: 'Instructor(s)',
    propName: 'instructorString',
    styles: {
        width: '127px',
    },
}, {
    propName: 'edit',
    isLabelHidden: true,
    styles: {
        width: '24px',
        margin: '0 0 0 0',
    },
}]

const sortSettings = ref<ITableSortSettings>({ column: tableColumns[0], direction: 1 })

const isLoading = ref(false)
const currentPage = ref(0)
const perPage = ref(20)
const showAddIntegrationToCourseSidePanel = ref(false)
const showEditCourseIntegrationSidePanel = ref(false)
const showUnarchiveIntegrationSidePanel = ref(false)
const hubspotMeetingUrl = ref('https://meetings.hubspot.com/deon-mccormick/integrations')
const activeTab = ref<'activeCourses' | 'archivedCourses'>('activeCourses')


const editIntegrationOrgExamId = ref('')
const editIntegrationObjectId = ref('')
const editIntegrationInstructors = ref<string[]>([])
// Need to pass the label AND value here for the side panel
const editIntegrationGroup = ref({
    value: '',
    label: '',
})
const unarchiveIntegrationOrgExamId = ref('')
const unarchiveIntegrationObjectId = ref('')
const unarchiveIntegrationInstructors = ref<string[]>([])
const unarchiveIntegrationGroup = ref({
    value: '',
    label: '',
})

const updateSortSettings = (sortSettingsVal: ITableSortSettings) => {
    sortSettings.value = sortSettingsVal
}

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const isPremium = computed(() => {
    return organizationModule.getters.getCurrentOrganization()?.enabledPremiumFeatures?.includes('lmsIntegration')
})

const currentIntegrations = computed(() => {
    return integrationsModule.getters.getCurrentIntegrations()
})

const currentCourseIntegrations = computed(() => {
    return integrationsModule.getters.getCurrentCourseIntegrations()
})

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const currentOrgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const courseIntegrationLibrary = computed(() => {
    return Object.entries(currentOrgGroups.value).reduce((acc, [ orgGroupId, orgGroup ]) => {
        const integrations = orgGroup.lmsCourseIntegrations
        if (!integrations) {
            // Skip group since no integrations
            return acc
        }
        integrations.forEach(i => {
            if (!acc[i.objectId]) {
                // Don't have an entry in the library for this course integration
                acc[i.objectId] = {
                    orgGroupId,
                    orgExamId: orgGroup.orgExam.objectId,
                    instructors: [],
                }
            }
            if (orgGroup.owner && orgGroup.type === 'system') {
                // OrgGroup has an owner and is tied to the current integration, so add them as an instructor!
                acc[i.objectId]?.instructors.push(orgGroup.owner.objectId)
            }
            if (orgGroup.type !== 'system') {
                const enteredGroup = acc[i.objectId]
                if (enteredGroup) {
                    // Already have an entry but found a group that's not the system group
                    enteredGroup.orgGroupId = orgGroup.objectId
                }
            }
        })
        return acc
    }, {} as CourseIntegrationLib)
})

const mappedIntegrations = computed(() => {
    const sortSettingsVal = sortSettings.value
    return Object.values(currentCourseIntegrations.value || [])
        .reduce((acc, cci) => {
        // Activated by display string
            const activatedByInstructor = currentOrgInstructors.value?.[cci.activatedBy.objectId]

            let activatedByDisplayString = '--'

            if (activatedByInstructor) {
                const activatedBy = currentOrgInstructorUsers.value?.[activatedByInstructor.user.objectId]
                activatedByDisplayString = (activatedBy?.firstName && activatedBy.lastName)
                    ? `${activatedBy.firstName} ${activatedBy.lastName}`
                    : `${activatedBy?.email}`
            }

            const courseIntegration = courseIntegrationLibrary.value[cci.objectId]

            if (!courseIntegration) {
                // Make sure we have an entry in the library
                return acc
            }

            // Exam display string
            const integrationOrgExamId = courseIntegration.orgExamId
            const examDisplayString = currentOrgExams.value[integrationOrgExamId]?.displayName || ''

            // Group display string
            const orgGroupForIntegration = currentOrgGroups.value[courseIntegration.orgGroupId]
            const groupDisplayString = orgGroupForIntegration?.type === 'system'
                ? '--'
                : orgGroupForIntegration?.name || ''

            // Instructor display string
            const instructorsForIntegration = courseIntegration.instructors

            // Normally wouldn't follow this pattern but the logic felt too complex to have in a ternary
            let instructorString = '--'

            if (instructorsForIntegration.length === 1 && instructorsForIntegration[0]) {
                // Build the instructor string
                const instructor = currentOrgInstructors.value?.[instructorsForIntegration[0]]

                if (instructor) {
                    const instructorUser = currentOrgInstructorUsers.value?.[instructor.user.objectId]

                    instructorString = (instructorUser?.firstName && instructorUser.lastName)
                        ? `${instructorUser.firstName} ${instructorUser.lastName}`
                        : `${instructorUser?.email}`
                }
            } else if (instructorsForIntegration.length > 1) {
                instructorString = `${instructorsForIntegration.length} Instructors`
            }

            const styles: { [key: string]: string } = {}
            if (rowHoverId.value === cci.objectId) {
                styles.backgroundColor = `${BrandColors.grayBackground}60`
            }

            acc.push({
                id: cci.objectId,
                styles,
                courseName: cci.name,
                activatedBy: activatedByDisplayString,
                exam: examDisplayString,
                orgExamId: integrationOrgExamId,
                group: groupDisplayString,
                orgGroupId: orgGroupForIntegration?.objectId || '',
                instructorIds: instructorsForIntegration,
                instructorString,
                isArchived: cci.isArchived,
            })
            return acc
        }, [] as MappedIntegration[])
        .sort((a, b) => {
            if (sortSettingsVal?.column?.name === 'Course Name') {
                return sortSettingsVal.direction * a.courseName.localeCompare(b.courseName)
            }
            if (sortSettingsVal?.column?.name === 'Activated By') {
                return sortSettingsVal.direction * a.activatedBy.localeCompare(b.activatedBy)
            }
            if (sortSettingsVal?.column?.name === 'Exam') {
                return sortSettingsVal.direction * a.exam.localeCompare(b.exam)
            }
            return -1
        })
})

const activeTabIntegrations = computed(() => {
    return mappedIntegrations.value.filter(mi => {
        return (activeTab.value === 'archivedCourses' && mi.isArchived)
            || (activeTab.value === 'activeCourses' && !mi.isArchived)
    })
})

const pagedCourseIntegrations = computed(() => {
    const perPageVal = perPage.value
    return activeTabIntegrations.value
        .slice(currentPage.value * perPageVal, (currentPage.value * perPageVal) + perPageVal)
})

const emptyStateTitle = computed(() => {
    return 'You have not added a course'
})

const emptyStateMessage = computed(() => {
    return 'Choose which Canvas courses to display the Pocket Prep SSO link on.'
})

onMounted(async () => {
    isLoading.value = true
    await Promise.all([
        organizationModule.actions.fetchCurrentOrganization(),
        instructorModule.actions.fetchCurrentOrgInstructor(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        integrationsModule.actions.fetchLMSIntegrations(),
        groupModule.actions.fetchCurrentOrgGroups(),
    ])
    await integrationsModule.actions.fetchLMSCourseIntegrations()

    isLoading.value = false
})

const editIntegration = (params: IntegrationActionParams) => {
    showEditCourseIntegrationSidePanel.value = true
    const { orgExamId, courseIntegrationId, instructorIds, group } = params

    // Set these component level variables so that the side panel can access them as props
    editIntegrationOrgExamId.value = orgExamId
    editIntegrationObjectId.value = courseIntegrationId
    editIntegrationInstructors.value = instructorIds
    editIntegrationGroup.value = group
}

const unarchiveIntegration = (params: IntegrationActionParams) => {
    showUnarchiveIntegrationSidePanel.value = true
    const { orgExamId, courseIntegrationId, instructorIds, group } = params

    // Set these component level variables so that the side panel can access them as props
    unarchiveIntegrationOrgExamId.value = orgExamId
    unarchiveIntegrationObjectId.value = courseIntegrationId
    unarchiveIntegrationInstructors.value = instructorIds
    unarchiveIntegrationGroup.value = group
}

const changePage = (newPage: number) => {
    currentPage.value = newPage
}

const chatWithSales = () => {
    if (currentOrganization.value && import.meta.env.VUE_APP_HUBSPOT_APP_ID && !isSupportWindow()) {
        // Fire amplitude event
        analyticsModule.actions.amplitudeTrack('CTA Clicked', {
            ctaLabel: 'Chat with Sales',
            ctaPlacement: 'Integrations',
            organizationId: currentOrganization.value.objectId,
            organizationName: currentOrganization.value.name,
        })

        // Open hubspot calendar link in a new tab
        window.open(hubspotMeetingUrl.value)
    }
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = row.id
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

const hideCellTooltip = () => {
    cellHovered.value = null
}

const showCellTooltip = (propName: string, id: string) => {
    cellHovered.value = {
        propName,
        id,
    }
}
</script>

<template>
    <div
        class="integrations">
        <AddIntegrationToCourseSidePanel
            v-if="showAddIntegrationToCourseSidePanel"
            :client-id="currentIntegrations[0]?.clientId"
            :lms-id="currentIntegrations[0]?.objectId"
            @close="showAddIntegrationToCourseSidePanel = false"
        />
        <EditCourseIntegrationSidePanel
            v-if="showEditCourseIntegrationSidePanel"
            :client-id="currentIntegrations[0]?.clientId"
            :lms-id="currentIntegrations[0]?.objectId"
            :course-integration-id="editIntegrationObjectId"
            :org-exam-id="editIntegrationOrgExamId"
            :instructors="editIntegrationInstructors"
            :existing-group="editIntegrationGroup"
            @close="showEditCourseIntegrationSidePanel = false"
        />
        <UnarchiveIntegrationSidePanel
            v-if="showUnarchiveIntegrationSidePanel"
            :client-id="currentIntegrations[0]?.clientId"
            :course-integration-id="unarchiveIntegrationObjectId"
            :org-exam-id="unarchiveIntegrationOrgExamId"
            :instructors="unarchiveIntegrationInstructors"
            :group="unarchiveIntegrationGroup"
            @close="showUnarchiveIntegrationSidePanel = false"
        />
        <div class="integrations__title">
            Integrations
            <Tag
                :is-beta-tag="true"
                class="integrations__beta-tag"
            >
                <template #tagMessage>
                    Beta
                </template>
            </Tag>
        </div>
        <div
            v-if="!isPremium"
            class="integrations__chat-with-sales"
        >
            <EmptyState
                class="integrations__empty"
                title="Available to Premium customers"
                message="Let's discuss adding Canvas Integrations to your Pocket Prep account."
            >
                <template #imageRow>
                    <img
                        class="integrations__image"
                        src="@/assets/images/pending-student-empty.png"
                    >
                </template>
            </EmptyState>
            <PocketButton
                class="integrations__chat-with-sales-btn"
                @click="chatWithSales"
            >
                Chat with Sales
            </PocketButton>
        </div>
        <div
            v-else
            class="integrations__premium"
        >

            <Icon
                v-if="isLoading"
                type="loading2"
                class="integrations__loading"
            />
            <div v-else>
                <IntegrationCard
                    v-if="!currentIntegrations.length"
                    class="integrations__empty-card"
                    :integration-active="false"
                />
                <div
                    v-else
                    class="integrations__cards"
                >
                    <div
                        v-for="integration in currentIntegrations"
                        :key="integration.objectId"
                    >
                        <IntegrationCard
                            class="integration__card"
                            :integration-active="true"
                            :integration-id="integration.objectId"
                        />
                    </div>
                </div>
            </div>
            <div
                v-if="currentIntegrations.length"
                class="integrations__table"
            >
                <div class="integrations__tabs">
                    <div
                        class="integrations__tab"
                        :class="{ active: activeTab === 'activeCourses' }"
                        @click="activeTab = 'activeCourses'"
                    >
                        Canvas Courses ({{ mappedIntegrations.filter(mi => !mi.isArchived).length }})
                    </div>
                    <div
                        class="integrations__tab"
                        :class="{ active: activeTab === 'archivedCourses' }"
                        @click="activeTab = 'archivedCourses'"
                    >
                        Archived ({{ mappedIntegrations.filter(mi => mi.isArchived).length }})
                    </div>
                </div>
                <Table
                    class="integrations__courses"
                    theme="open"
                    :default-sort="sortSettings"
                    :sort-toggle-order="[ 1, -1 ]"
                    :columns="tableColumns"
                    :show-header="false"
                    :table-row-styles="{
                        paddingRight: '0',
                        height: '45px',
                    }"
                    :table-cell-styles="{
                        position: 'relative',
                    }"
                    :rows="pagedCourseIntegrations"
                    @sort="updateSortSettings($event)"
                    @rowEnter="rowEnter"
                    @rowLeave="rowLeave"
                >
                    <template #tableCellValue="{ row, column }">
                        <div
                            v-if="column.propName === 'courseName'"
                            class="integrations__course-name"
                        >
                            <OverflowTooltip>
                                {{ row.courseName }}
                            </OverflowTooltip>
                        </div>
                        <template v-else-if="column.propName === 'edit'">
                            <Tooltip
                                v-if="cellHovered
                                    && (cellHovered.propName === column.propName && cellHovered.id === row.id)"
                                class="integrations__edit-tooltip"
                            >
                                {{ row.isArchived ? 'Unarchive Course Integration' : 'Edit Course Integration' }}
                            </Tooltip>
                            <Icon
                                v-show="rowHoverId === row.id && (!('hideRowEdit' in row) || !row.hideRowEdit)"
                                class="integrations__edit"
                                type="edit"
                                @mouseover="showCellTooltip(column.propName, row.id)"
                                @mouseout="hideCellTooltip"
                                @click="row.isArchived
                                    ? unarchiveIntegration({
                                        orgExamId: row.orgExamId,
                                        courseIntegrationId: row.id,
                                        instructorIds: row.instructorIds,
                                        group: {
                                            label: row.group,
                                            value: row.orgGroupId,
                                        },
                                    })
                                    : editIntegration({
                                        orgExamId: row.orgExamId,
                                        courseIntegrationId: row.id,
                                        instructorIds: row.instructorIds,
                                        group: {
                                            label: row.group,
                                            value: row.orgGroupId,
                                        },
                                    })"
                            />
                        </template>
                    </template>
                    <template #tableRows>
                        <Icon
                            v-if="isLoading"
                            type="loading2"
                            class="integrations__loading"
                        />
                        <div v-if="!activeTabIntegrations?.length && activeTab === 'activeCourses' && !isLoading">
                            <EmptyState
                                class="integrations__empty"
                                :title="emptyStateTitle"
                                :message="emptyStateMessage"
                            >
                                <template #imageRow>
                                    <div />
                                </template>
                            </EmptyState>
                            <PocketButton
                                class="integrations__add-integration-btn"
                                @click="showAddIntegrationToCourseSidePanel = true"
                            >
                                Add Integration to Course
                            </PocketButton>
                        </div>
                    </template>
                </Table>
                <div
                    v-if="activeTabIntegrations?.length && activeTab === 'activeCourses'"
                    class="integrations__lower-container"
                >
                    <PocketButton
                        v-if="!isLoading"
                        type="secondary"
                        class="integrations__add-integration-btn--bottom"
                        @click="showAddIntegrationToCourseSidePanel = true"
                    >
                        + Add Integration to Course
                    </PocketButton>
                    <TablePagination
                        v-if="pagedCourseIntegrations.length"
                        class="integrations__pagination"
                        unit="Transactions"
                        :per-page="perPage"
                        :current-page="currentPage"
                        :total="mappedIntegrations.length"
                        @changePage="changePage"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>


.integrations {
    &__title {
        font-size: 26px;
        font-weight: 600;
        display: flex;
    }

    &__beta-tag {
        margin: 6px 0 0 12px;
    }

    &__image {
        width: 65px;
        height: 92px;
        margin-bottom: 15px;
    }

    &__chat-with-sales-btn {
        display: table;
        margin: 22px auto;
    }

    &__empty-card {
        margin-top: 44px;
    }

    &__cards {
        margin-top: 44px;
        display: flex;
        flex-wrap: wrap;
        row-gap: 15px;
        justify-content: space-between;
    }

    &__card {
        margin-top: 5px;
    }

    &__tabs {
        border-bottom: 1px solid $gray-divider;
        display: flex;
        font-size: 16px;
        line-height: 22px;
        font-weight: 500;
        margin: 72px 0 15px;
    }

    &__tab {
        position: relative;
        padding: 6px 8px;
        cursor: pointer;
        user-select: none;
        margin-right: 9px;

        &.active {
            font-weight: 600;

            &::after {
                content: '';
                width: 100%;
                height: 2px;
                bottom: -2px;
                left: 0;
                position: absolute;
                background: $brand-blue;
            }
        }
    }

    &__empty {
        margin: 0 auto;
        padding: 40px 0 0;
        min-width: 300px;
    }

    &__add-integration-btn {
        display: table;
        margin: 22px auto;

        &--bottom {
            display: flex;
        }
    }

    &__loading {
        margin: 80px auto;
    }

    &__lower-container {
        display: flex;
        justify-content: space-between;
    }

    &__pagination {
        padding-top: 16px;
    }

    &__edit {
        cursor: pointer;
        height: 24px;
        color: $brand-blue;
    }

    &__edit-tooltip {
        position: absolute;
        transform: translate(0, -100%);
        left: 12px;
        top: -3px;
    }
}
</style>
